import { Component, OnInit, ChangeDetectorRef, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { SharedModule } from '@shared/shared_module';
import { TableComponent, TableColumn, FilterConfigType, FilterOption, FilterOptionsLazyLoadEvent, TableButton } from '@shared/components/table/table.component';
import { ManageAssessmentApiService, ManageAssessment } from '@core/services/api/manage-assessment-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Subject } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AssessmentConfirmationPopupComponent, ConfirmationPopupConfig } from '../assessment-confirmation-popup/assessment-confirmation-popup.component';
import { AssessmentSharePopupComponent } from "../assessment-share-popup/assessment-share-popup.component";
import { ManageAssessmentSuccessPopupComponent } from "../manage-assessment-success-popup/manage-assessment-success-popup.component";
import { AuditPermissionService } from '@core/services/audit-permission.service';
import { AssessmentDueDateExtensionPopupComponent } from '../assessment-due-date-extension-popup/assessment-due-date-extension-popup.component';

type AssessmentTabStatus = 'total' | 'draft' | 'submitted' | 'completed' | 'cancelled' | 'yetToStart' | 'ongoing' | 'expired';

type ConfirmationActionType = 'revise' | 'markAsCompleted' | 'cancelAssessment' | 'no_impact' | 'delete' | 'duplicate' | 'duplicate_draft';
export interface AssessmentCounts {
  total: number;
  draft: number;
  submitted: number;
  completed: number;
  cancelled_deleted: number;
  yet_to_start: number;
  ongoing: number;
  expired: number;
}

@Component({
  selector: "app-manage-assessment-tab",
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    SharedModule,
    AssessmentConfirmationPopupComponent,
    AssessmentSharePopupComponent,
    ManageAssessmentSuccessPopupComponent,
    AssessmentDueDateExtensionPopupComponent
  ],
  templateUrl: "./manage-assessment-tab.component.html",
  styleUrls: ["./manage-assessment-tab.component.scss"],
  providers: [DatePipe],
})
export class ManageAssessmentTabComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() activeTab: AssessmentTabStatus = "total";
  @Output() resumeRca = new EventEmitter<any>();
  // New Output to emit counts to the parent
  @Output() assessmentCountsUpdated = new EventEmitter<AssessmentCounts>();
  requestData: any[] = [];
  requestColumns: TableColumn[] = [];
  requestGlobalFilterFields: string[] = [];
  requestFiltersConfig: FilterConfigType[] = [];
  requestFiltersValue: { [key: string]: any } = {};

  requestActions: any[] = [];

  requestTotalRecords: number = 0;
  requestLoading: boolean = false;
  requestPage: number = 1;
  requestPageSize: number = 10;
  requestLastLazyLoadEvent: LazyLoadEvent | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly filterOptionsLazyLoad$ = new Subject<FilterOptionsLazyLoadEvent>();
  private readonly globalRaUsersRequest$ = new Subject<{ page: number; search: string; append: boolean }>();

  showConfirmationPopup: boolean = false;
  confirmationConfig: ConfirmationPopupConfig | null = null;
  private pendingAction: {
    type: ConfirmationActionType;
    row: ManageAssessment;
  } | null = null;
  selectedAssessmentItems: any[] = [];

  showBulkActionButton: boolean = false;
  bulkActionButtonConfig: TableButton = {
    label: "No Impact",
    action: "bulkAction",
  };
  UserRole = {
    RAAdmin: "RA Admin",
    LocalRA: "Local RA (Bayer RA colleague)",
    LocalRA1: "Local RA (Dealer & Distributor)",
    GlobalRA: "Global RA",
  };
  currentUserRole: any = "";
  tableSelectionMode: any = "single";
  // New properties for the Share popup
  showSharePopup: boolean = false;
  sharePopupConfig: any | null = null;
  currentAssessmentToShare: ManageAssessment | null = null; // Make this public
  successPopupVisible = false;
  successPopupData: any;
  showDueDateExtensionPopup: boolean = false;

  // Add a property to store the global search term
  currentGlobalSearchTerm: string = '';
  currentUserId: number | null = null;
  globalRaFilterOptions: FilterOption[] = [];
  globalRaFilterPage: number = 1;
  globalRaFilterPageSize: number = 10 //100;
  globalRaFilterSearch: string = '';
  globalRaFilterLoading: boolean = false;
  globalRaFilterMeta: { page?: number; total_pages?: number } = {};

  canShowActionButtons(rowData: any): boolean {
    const isCurrentUser = rowData?.owner_user_id === this.currentUserId;
    const isSharedUser = rowData?.shared_user_ids?.includes(this.currentUserId);
    // Only show if status is 'Yet to Start' AND current user is one of the local RAs
    return isSharedUser || isCurrentUser || this.currentUserRole === this.UserRole.RAAdmin;
  }

  // New function to determine if delete button should be disabled
  isDeleteButtonDisabled(rowData: any): boolean {
    // Delete button should be disabled if the current user is not the owner
    return rowData?.owner_user_id !== this.currentUserId;
  }

  // New function to determine if revise button should be visible
  isReviseButtonVisible(rowData: any): boolean {
    // Revise button should be visible if the current user is in the shared_user_ids array
    return rowData?.shared_user_ids?.includes(this.currentUserId) || false;
  }

  // Only users present in the shared_user_ids list can share an assessment
  isShareButtonDisabled(rowData: any): boolean {
    return !this.isCurrentUserShared(rowData);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private manageAssessmentApiService: ManageAssessmentApiService,
    private auditPermissionService: AuditPermissionService,
    private datePipe: DatePipe,
    private messageService: MessageService,
    private router: Router
  ) { }

  ngOnInit(): void {
    const storageItem: any = localStorage.getItem("profile");
    let parsedProfile: any = null;
    try {
      parsedProfile = storageItem ? JSON.parse(storageItem) : null;
    } catch {
      parsedProfile = null;
    }
    this.currentUserRole = parsedProfile?.userRole || "";
    const parsedUserId = parsedProfile?.userId;
    if (parsedUserId === undefined || parsedUserId === null || parsedUserId === "") {
      this.currentUserId = null;
    } else {
      const numericUserId = Number(parsedUserId);
      this.currentUserId = Number.isNaN(numericUserId) ? null : numericUserId;
    }
    this.filterOptionsLazyLoad$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((event) => this.onFilterOptionsLazyLoad(event));
    this.globalRaUsersRequest$
      .pipe(
        switchMap(({ page, search, append }) => {
          this.globalRaFilterLoading = true;
          this.updateGlobalRaFilterConfig();

          return this.auditPermissionService
            .getGlobalUserName(page, this.globalRaFilterPageSize, search)
            .pipe(
              tap((response: any) => {
                const nextOptions = (response?.users || []).map((user: any) => ({
                  label: user.name + " (" + user.email + ")",
                  value: String(user.id),
                }));

                this.globalRaFilterOptions = append
                  ? [...this.globalRaFilterOptions, ...nextOptions.filter((option: FilterOption) =>
                    !this.globalRaFilterOptions.some((existing) => existing.value === option.value)
                  )]
                  : nextOptions;
                this.globalRaFilterPage = response?.meta?.page ?? page;
                this.globalRaFilterMeta = response?.meta || {};
                this.globalRaFilterSearch = search;
                this.globalRaFilterLoading = false;
                this.updateGlobalRaFilterConfig();
                this.cdr.detectChanges();
              }),
              catchError((error: HttpErrorResponse) => {
                this.globalRaFilterOptions = [];
                this.globalRaFilterLoading = false;
                this.updateGlobalRaFilterConfig();
                this.messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail:
                    error.error?.message ||
                    "Failed to load Global RA filter options. Please try again.",
                  life: 5000,
                });
                return EMPTY;
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
    this.fetchGlobalRaUsers();
    this.initializeFilterValues();
    this.setRequestsTabConfig();
    this.loadSignupRequests({ first: 0, rows: this.requestPageSize });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["activeTab"] && !changes["activeTab"].firstChange) {
      this.initializeFilterValues();
      this.setRequestsTabConfig();
      this.requestLastLazyLoadEvent = { first: 0, rows: this.requestPageSize };
      this.loadSignupRequests(this.requestLastLazyLoadEvent);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes or resets the filter values based on the active tab.
   * This method is called on ngOnInit and whenever the activeTab changes.
   */
  initializeFilterValues(): void {
    this.requestFiltersValue = {};
    this.requestFiltersConfig = [
      {
        key: "status",
        label: "Status",
        type: "multiselect",
        options: [
          { label: "Draft", value: "Draft" },
          { label: "Submitted", value: "Submitted" },
          { label: "Completed", value: "Completed" },
          { label: "Cancelled", value: "Cancelled" },
          { label: "Deleted", value: "Deleted" },
          { label: "Expired", value: "Expired" },
        ],
      },
      {
        key: "start_date",
        label: "Start Date Range",
        type: "daterange",
      },
      {
        key: "due_date",
        label: "Due Date Range",
        type: "daterange",
      },
    ];

    this.requestFiltersConfig.push({
      key: "global_ra",
      label: "Global RA",
      type: "multiselect",
      options: this.globalRaFilterOptions,
      lazy: true,
      loading: this.globalRaFilterLoading,
      pageSize: this.globalRaFilterPageSize,
      virtualScroll: true,
      virtualScrollItemSize: 40,
    });

    switch (this.activeTab) {
      case "draft":
        this.requestFiltersValue["status"] = [
          { label: "Draft", value: "Draft" },
        ];
        break;
      case "submitted":
        this.requestFiltersValue["status"] = [
          { label: "Submitted", value: "Submitted" },
        ];
        break;
      case "completed":
        this.requestFiltersValue["status"] = [
          { label: "Completed", value: "Completed" },
        ];
        break;
      case "cancelled":
        this.requestFiltersValue["status"] = [
          { label: "Cancelled", value: "Cancelled" },
          { label: "Deleted", value: "Deleted" },
        ];
        break;
      case "yetToStart":
        this.requestFiltersValue["status"] = [
          { label: "Yet to Start", value: "Yet to Start" },
        ];
        break;
      case "ongoing":
        this.requestFiltersValue["status"] = [
          { label: "Ongoing", value: "Ongoing" },
        ];
        break;
      case "expired":
        this.requestFiltersValue["status"] = [
          { label: "Expired", value: "Expired" },
        ];
        break;
      case "total":
      default:
        break;
    }
    this.cdr.detectChanges();
  }

  fetchGlobalRaUsers(page: number = 1, search: string = '', append: boolean = false): void {
    this.globalRaUsersRequest$.next({ page, search, append });
  }

  onFilterOptionsLazyLoad(event: any): void {
    if (event.key !== 'global_ra') {
      return;
    }

    const requestedSearch = event.filter ?? this.globalRaFilterSearch;
    const requestedRows = event.rows ?? this.globalRaFilterPageSize;
    const firstVisibleIndex = event.first ?? 0;
    const isSearchChange = requestedSearch !== this.globalRaFilterSearch;

    this.globalRaFilterPageSize = requestedRows;

    if (isSearchChange) {
      this.globalRaFilterPage = 1;
      this.globalRaFilterMeta = {};
      this.fetchGlobalRaUsers(1, requestedSearch, false);
      return;
    }

    if (this.globalRaFilterLoading) {
      return;
    }

    const totalPages = this.globalRaFilterMeta.total_pages ?? 1;
    if (firstVisibleIndex === 0 && this.globalRaFilterOptions.length === 0) {
      this.fetchGlobalRaUsers(1, requestedSearch, false);
      return;
    }

    const preloadThreshold = Math.min(1, Math.max(1, Math.ceil(requestedRows / 2))) // 10;
    const hasReachedLoadedEnd =
      firstVisibleIndex > 0 &&
      firstVisibleIndex + requestedRows >=
      this.globalRaFilterOptions.length - preloadThreshold;
    const nextPage = this.globalRaFilterPage + 1;

    if (nextPage > totalPages) {
      return;
    }

    if (hasReachedLoadedEnd) {
      this.fetchGlobalRaUsers(nextPage, requestedSearch, true);
    }
  }

  private updateGlobalRaFilterConfig(): void {
    const globalRaFilter = this.requestFiltersConfig.find(
      (filter) => filter.key === 'global_ra'
    );

    if (!globalRaFilter) {
      return;
    }

    globalRaFilter.options = this.globalRaFilterOptions;
    globalRaFilter.loading = this.globalRaFilterLoading;
    globalRaFilter.pageSize = this.globalRaFilterPageSize;
  }

  /**
   * Handles lazy load events from the PrimeNG table component.
   * Stores the event and triggers data loading.
   * @param event The LazyLoadEvent object from PrimeNG table.
   */
  onLazyLoadEventFromTable(event: LazyLoadEvent): void {
    this.requestLastLazyLoadEvent = event;
    this.loadSignupRequests(event);
  }

  /**
   * Sets the column definitions and global filter fields for the table.
   * This is called on ngOnInit and whenever the activeTab changes.
   */
  setRequestsTabConfig(): void {
    this.requestColumns = [
      { field: "s_no", header: "S.No", sortable: false },
      {
        field: "version_number",
        header: "Version",
        sortable: false,
        hasVersionIcon: true,
      },
      {
        field: "change_assess_no",
        header: "Change Assessment Number",
        sortable: false,
      },
      {
        field: "title",
        header: "Title",
        sortable: false,
        truncateAt: 20,
      },
      { field: "ecn_ern", header: "ECR/ECN Number", sortable: false, truncateAt: 20 },
      { field: "change_types", header: "Type of Change", sortable: false, truncateAt: 20 },
      { field: "product_families", header: "Product Family", sortable: false, truncateAt: 20 },
    ];

    this.requestColumns.push({ field: "global_ra", header: "Global RA", sortable: false, truncateAt: 20 });

    this.requestColumns.push(
      { field: "start_date", header: "Start Date", sortable: false },
      { field: "due_date", header: "Due Date", sortable: false },
      {
        field: "display_status",
        header: "Status",
        sortable: false,
        type: "status",
        isStatus: true,
      }
    );

    if (
      (this.activeTab === "total" || this.activeTab === "submitted") &&
      this.currentUserRole !== this.UserRole.LocalRA &&
      this.currentUserRole !== this.UserRole.LocalRA1
    ) {
      this.requestColumns.push({
        field: "display_progress",
        header: "Progress",
        sortable: false,
        type: "progress",
        isProgress: true,
      });
    }
    this.requestColumns.push({
      field: "actions",
      header: "Actions",
      sortable: false,
      isCustomButton: true,
    });

    this.requestGlobalFilterFields = [
      "change_assess_no",
      "title",
      "ecn_ern",
      "change_types",
      "product_families",
      "status",
      "global_ra",
    ];

    this.requestActions = this.getActionsForTab(this.activeTab);
    this.cdr.detectChanges();
  }

  /**
   * Returns the list of available actions for table rows based on the current active tab.
   * @param tab The current active tab status.
   * @returns An array of action objects.
   */
  private getActionsForTab(tab: AssessmentTabStatus): any[] {
    switch (tab) {
      case "total":
      case "submitted":
        return [
          { label: "Resume", action: "Resume", icon: "pi pi-play" },
          {
            label: "Mark as Completed",
            action: "Mark as Completed",
            icon: "pi pi-check-circle",
          },
          {
            label: "Cancel Assessment",
            action: "Cancel Assessment",
            icon: "pi pi-times-circle",
          },
        ];
      case "draft":
        return [
          { label: "Resume", action: "Resume", icon: "pi pi-play" },
          { label: "Delete", action: "Delete", icon: "pi pi-trash" },
        ];
      case "completed":
        return [
          { label: "View Details", action: "View Details", icon: "pi pi-eye" },
        ];
      case "cancelled":
        return [
          { label: "View Details", action: "View Details", icon: "pi pi-eye" },
          { label: "Revise", action: "Revise", icon: "pi pi-pencil" },
          { label: "Delete", action: "Delete", icon: "pi pi-trash" },
        ];
      case "expired":
        return [
          { label: "View Details", action: "View Details", icon: "pi pi-eye" },
        ];
      default:
        return [];
    }
  }

  /**
   * Formats a date string to 'dd/MM/yyyy' format.
   * @param dateString The date string to format.
   * @returns Formatted date string or 'N/A'.
   */
  formatDate(dateString: string): string {
    if (!dateString) return "N/A";
    return this.datePipe.transform(dateString, "dd/MM/yyyy") || "N/A";
  }

  /**
   * Loads signup requests from the API based on current filters and pagination.
   * This is the core data fetching method.
   * @param event Optional LazyLoadEvent for pagination and sorting.
   */
  loadSignupRequests(event?: LazyLoadEvent): void {
    this.requestLoading = true;
    this.cdr.detectChanges();

    const currentEvent = event ||
      this.requestLastLazyLoadEvent || { first: 0, rows: this.requestPageSize };
    this.requestPage =
      (currentEvent.first ?? 0) / (currentEvent.rows ?? this.requestPageSize) +
      1;
    this.requestPageSize = currentEvent.rows ?? this.requestPageSize;

    const filtersToSend: any = {};
    // Iterate over the current `requestFiltersValue` to build the API payload
    for (const key in this.requestFiltersValue) {
      if (this.requestFiltersValue.hasOwnProperty(key)) {
        const value = this.requestFiltersValue[key];
        const filterConfig = this.requestFiltersConfig.find(
          (f) => f.key === key
        );
        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          if (
            key === "status" &&
            filterConfig?.type === "multiselect" &&
            Array.isArray(value)
          ) {
            filtersToSend[`${key}_filter`] = value
              .map((item) => item.value)
              .join(",");
            filtersToSend[`aggregated_${key}`] = value
              .map((item) => item.value)
              .join(",");
          } else if (
            key === "global_ra" &&
            filterConfig?.type === "multiselect" &&
            Array.isArray(value)
          ) {
            filtersToSend['global_ra_user_ids'] = value
              .map((item: any) => item?.value ?? "")
              .filter((item: any) => item !== null && item !== undefined && item !== "").join(",");
          } else if (key === "start_date" && Array.isArray(value)) {
            filtersToSend["issue_date_from"] = value[0]
              ? this.datePipe.transform(value[0], "yyyy-MM-dd") || null
              : null;
            filtersToSend["issue_date_to"] = value[1]
              ? this.datePipe.transform(value[1], "yyyy-MM-dd") || null
              : null;
          } else if (key === "due_date" && Array.isArray(value)) {
            filtersToSend[`${key}_from`] = value[0]
              ? this.datePipe.transform(value[0], "yyyy-MM-dd") || null
              : null;
            filtersToSend[`${key}_to`] = value[1]
              ? this.datePipe.transform(value[1], "yyyy-MM-dd") || null
              : null;
          } else {
            filtersToSend[key] = value;
          }
        }
      }
    }

    // Add the global search term to filtersToSend if it exists
    if (this.currentGlobalSearchTerm) {
      filtersToSend['search'] = this.currentGlobalSearchTerm;
    }

    // Call the API service to get assessment data
    this.manageAssessmentApiService
      .getManageAssessment(
        this.requestPage,
        this.requestPageSize,
        filtersToSend
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Calculate counts from the full response data
          (this.currentUserRole === this.UserRole.LocalRA || this.currentUserRole === this.UserRole.LocalRA1) ? this.calculateAndEmitCounts(response.aggregated_status_counts) : this.calculateAndEmitCounts(response.status_counts);
          // Map the API response to the table's data format
          this.requestData = response.rcas.map((req, index) => {
            req.version_number = req?.version_number
              ? `v${req.version_number}`
              : null;
            req.title = req?.title || 'N/A';
            req.ecn_ern = req?.ecn_ern || 'N/A';
            let displayStatus = req.status;
            let displayProgress = "";
            // Calculate and format progress string
            if (
              req.progress &&
              typeof req.progress.completed === "number" &&
              typeof req.progress.total === "number"
            ) {
              displayProgress = `${req.progress.completed}/${req.progress.total}`;
            }
            return {
              s_no: (this.requestPage - 1) * this.requestPageSize + index + 1,
              ...req,
              start_date: this.formatDate(req.start_date),
              due_date: this.formatDate(req.due_date),
              display_status: displayStatus,
              display_progress: displayProgress,
            };
          });
          this.requestTotalRecords = response.meta.total;
          this.requestLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.requestData = [];
          this.requestTotalRecords = 0;
          this.requestLoading = false;
          this.cdr.detectChanges();
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to load data. Please try again.",
            life: 5000,
          });
          // Emit zero counts on error
          this.assessmentCountsUpdated.emit({
            total: 0, draft: 0, submitted: 0, completed: 0, cancelled_deleted: 0, yet_to_start: 0, ongoing: 0, expired: 0
          });
        },
        complete: () => {
          if (this.requestLoading) {
            this.requestLoading = false;
            this.cdr.detectChanges();
          }
        },
      });
  }

  /**
   * Calculates the counts for each assessment status from the provided data
   * and emits them to the parent component.
   * @param rcas The array of ManageAssessment objects.
   */
  private calculateAndEmitCounts(status: any): void {
    const counts: AssessmentCounts = {
      total: status?.total || 0,
      draft: status?.draft || 0,
      submitted: status?.submitted || 0,
      completed: status?.completed || 0,
      cancelled_deleted: status?.cancelled_deleted || 0,
      yet_to_start: status?.yet_to_start || 0,
      ongoing: status?.ongoing || 0,
      expired: status?.expired || 0,
    };
    this.assessmentCountsUpdated.emit(counts);
  }

  /**
   * Handles search button clicks from the table's filter section.
   * Applies the new filters and reloads the data.
   * @param filters An object containing the applied filter values.
   */
  onRequestsSearchClicked(filters: { [key: string]: any }): void {
    const newFilters = { ...this.requestFiltersValue, ...filters };
    if (this.activeTab !== "total") {
      const tabStatusValue = this.requestFiltersValue["status"]?.[0]?.value;
      if (
        tabStatusValue &&
        newFilters["status"] &&
        Array.isArray(newFilters["status"])
      ) {
        if (
          !newFilters["status"].some((s: any) => s.value === tabStatusValue)
        ) {
          newFilters["status"].push({
            label: tabStatusValue,
            value: tabStatusValue,
          });
        }
      } else if (tabStatusValue) {
        newFilters["status"] = [
          { label: tabStatusValue, value: tabStatusValue },
        ];
      }
    }

    this.requestFiltersValue = newFilters;
    this.requestLastLazyLoadEvent = { first: 0, rows: this.requestPageSize };
    this.loadSignupRequests(this.requestLastLazyLoadEvent);
  }

  // New method to handle global search input from the table
  onGlobalSearch(searchTerm: string): void {
    this.currentGlobalSearchTerm = searchTerm;
    // Reset pagination to the first page when a global search is performed
    this.requestLastLazyLoadEvent = { first: 0, rows: this.requestPageSize };
    this.loadSignupRequests(this.requestLastLazyLoadEvent);
  }

  /**
   * Handles custom button clicks (actions) from the table rows.
   * @param event An object containing the action name and the row data.
   */
  onToggleAction(event: { action: string; row: ManageAssessment }): void {
    switch (event.action) {
      case "Resume":
        const eventObj = {
          row: event.row,
          action: event.action,
        };
        this.resumeRca.emit(eventObj);
        break;
      case "Revise":
        this.showConfirmation("revise", event.row);
        break;
      case "Extend Due Date":
        this.openDueDateExtensionPopup(event.row);
        break;
      case "Mark as Completed":
        this.showConfirmation("markAsCompleted", event.row);
        break;
      case "Cancel Assessment":
        this.showConfirmation("cancelAssessment", event.row);
        break;
      case "Delete":
        this.showConfirmation("delete", event.row);
        break;
      case "Share":
        this.showShareAssessmentPopup(event.row);
        break;
      case "Duplicate":
        this.showConfirmation("duplicate", event.row);
        break;
      case "Duplicate Draft":
        this.showConfirmation("duplicate_draft", event.row);
        break;
      case "View":
        if (event.row.rca_id) {
          this.router.navigate(["/manage-assessment", event.row.rca_id], {
            queryParams: { fromAction: true },
          });
        } else {
          this.messageService.add({
            severity: "warn",
            summary: "Warning",
            detail: "Cannot view details: RCA ID is missing.",
            life: 3000,
          });
        }
        break;
      default:
        console.warn("Unknown action:", event.action);
    }
  }

  openDueDateExtensionPopup(assessment: ManageAssessment): void {
    this.sharePopupConfig = {
      header: `Due Date Extension`,
      currentDueDate: assessment.due_date,
      assessmentId: assessment.rca_id,
    }
    this.showDueDateExtensionPopup = true;
  }

  onDueDateExtensionPopupClose() {
    this.showDueDateExtensionPopup = false;
  }

  dueDateConfirmed($event: any) {
    this.loadSignupRequests();
    this.onShowSuccessPopup({
      title: "Due Date Extension",
      message: `Due date has been modified.`,
      isSuccess: true,
    });
  }

  // New method to show the share assessment popup
  showShareAssessmentPopup(assessment: ManageAssessment): void {
    if (!this.isCurrentUserShared(assessment)) {
      this.messageService.add({
        severity: "warn",
        summary: "Action not allowed",
        detail: "Only users shared on this assessment can share it further.",
        life: 3000,
      });
      return;
    }
    this.currentAssessmentToShare = assessment;
    this.sharePopupConfig = {
      header: assessment?.ecn_ern ? `Share Assessment (ECR${assessment.ecn_ern})` : `Share Assessment`, // Use ECR/ECN number in header
      assessmentId: assessment.rca_id,
      globalRaOptions: [ // Example options, replace with actual data from API if available
        { label: 'John Doe', value: 'john.doe@example.com' },
        { label: 'David Smith', value: 'david.smith@example.com' },
        { label: 'Alice Johnson', value: 'alice.johnson@example.com' },
      ]
    };
    this.showSharePopup = true;
  }

  // New method to handle the share confirmation from the popup
  onShareConfirmed(event: any): void {
    if (this.currentAssessmentToShare) {
      this.onShowSuccessPopup({
        title: "Share RCA",
        isSuccess: true,
        message: `Assessment ${this.currentAssessmentToShare.change_assess_no} shared successfully!`,
        rcaData: event,
      });

      // Reload the assessment data to reflect the updated shared_user_ids
      this.loadSignupRequests(this.requestLastLazyLoadEvent || { first: 0, rows: this.requestPageSize });
    }
    this.currentAssessmentToShare = null; // Clear the assessment after sharing
  }

  onVersionSelected(event: { rcaId: string; versionDetail: any }): void {
    this.router.navigate(["/manage-assessment", event.rcaId], {
      queryParams: { fromVersion: true },
    });
    this.messageService.add({
      severity: "info",
      summary: "Version Selected",
      detail: `Navigating to RCA ID: ${event.rcaId}`,
      life: 3000,
    });
  }

  showConfirmation(actionType: ConfirmationActionType, row: any): void {
    this.pendingAction = { type: actionType, row: row };

    // Always reset the popup flag before opening
    this.showConfirmationPopup = false;
    this.cdr.detectChanges();

    switch (actionType) {
      case "revise":
        this.confirmationConfig = {
          header: "Revise Assessment",
          message: `Are you sure you want to revise this assessment for "${row.change_assess_no}" that you have already created?.`,
          acceptButtonLabel: "Yes",
          rejectButtonLabel: "Cancel",
          icon: "pi pi-exclamation-triangle",
          acceptButtonStyleClass: "p-button-warning",
        };
        break;
      case "markAsCompleted":
        this.confirmationConfig = {
          header: "Mark as completed",
          message: `Are you sure you want to mark this assessment for "${row.change_assess_no}" as 'Completed'?`,
          acceptButtonLabel: "Submit",
          rejectButtonLabel: "Cancel",
          icon: "pi pi-check-circle",
          acceptButtonStyleClass: "p-button-success",
        };
        break;
      case "cancelAssessment":
        this.confirmationConfig = {
          header: "Cancel Assessment",
          message: `Are you sure you want to cancel this assessment for "${row.change_assess_no}"?`,
          acceptButtonLabel: "Yes",
          rejectButtonLabel: "Cancel",
          icon: "pi pi-times-circle",
          acceptButtonStyleClass: "p-button-danger",
        };
        break;
      case "no_impact":
        this.confirmationConfig = {
          header: "No Impact Submission",
          message: `You are about to submit multiple assessments marking it as No Impact?.`,
          acceptButtonLabel: "Submit",
          rejectButtonLabel: "Cancel",
          icon: "pi pi-exclamation-triangle",
          acceptButtonStyleClass: "p-button-warning",
        };
        break;
      case "delete":
        this.confirmationConfig = {
          header: "Delete Assessment",
          message: `Are you sure you want to delete this assessment "${row.change_assess_no}"?`,
          acceptButtonLabel: "Yes",
          rejectButtonLabel: "No",
          icon: "pi pi-exclamation-triangle",
          acceptButtonStyleClass: "p-button-success",
        };
        break;
      case "duplicate":
        this.confirmationConfig = {
          header: "Duplicate Assessment",
          message: `Are you sure you want to duplicate this assessment "${row.change_assess_no}"?`,
          acceptButtonLabel: "Yes",
          rejectButtonLabel: "No",
          icon: "pi pi-check-circle",
          acceptButtonStyleClass: "p-button-success",
        };
        break;
      case "duplicate_draft":
        this.confirmationConfig = {
          header: "Edit duplicated Assessment",
          message: `A duplicate of your draft has been created. You can now edit it`,
          acceptButtonLabel: "Edit",
          rejectButtonLabel: "Close",
          icon: "",
          acceptButtonStyleClass: "p-button-success",
        };
        break;
      default:
        this.confirmationConfig = null;
        this.pendingAction = null;
        return;
    }
    // Now open the popup
    this.showConfirmationPopup = true;
    this.cdr.detectChanges();
  }

  onConfirmation(confirmed: any): void {
    if (confirmed && this.pendingAction) {
      const { type, row } = this.pendingAction;
      switch (type) {
        case "revise":
          this.executeRevise(row);
          break;
        case "markAsCompleted":
          this.executeMarkAsCompleted(row);
          break;
        case "cancelAssessment":
          this.executeCancelAssessment(row);
          break;
        case "no_impact":
          this.markAsNoImpact(row);
          break;
        case "delete":
          this.deleteAssessment(row);
          break;
        case "duplicate":
          this.duplicateAssessment(row);
          break;
        case "duplicate_draft":
          const actionItems = {
            row: row,
            action: 'Resume',
          };
          this.onToggleAction(actionItems);
          this.cdr.detectChanges();
          break;
      }
    }
    // Reset pending action and hide popup regardless of confirmation
    this.pendingAction = null;
    this.showConfirmationPopup = false;
  }

  private markAsNoImpact(row: any): void {
    const mappingId: number[] =
      row?.map((item: any) => item?.response_mapping_id) || [];
    const payloadObj = {
      response_mapping_ids: mappingId,
    };
    this.manageAssessmentApiService
      .updateRcaAsNoImpact(payloadObj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Mark as No Impact updated successfuly!`,
            life: 5000,
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to mark as No Impact:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to mark as No Impact. Please try again.",
            life: 5000,
          });
        },
      });
  }

  private deleteAssessment(row: any): void {
    const rcaId = row?.rca_id || null;
    this.manageAssessmentApiService
      .deleteRcaAssessment(rcaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.ngOnInit();
          this.onShowSuccessPopup({
            isSuccess: true,
            title: "Delete RCA",
            message: `RCA Deleted Successfully ${row?.change_assess_no}`,
            rcaData: response,
          })
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to delete RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to delete RCA. Please try again.",
            life: 5000,
          });
        },
      });
  }

  private duplicateAssessment(row: any): void {
    const rcaId = row?.rca_id || null;
    this.manageAssessmentApiService
      .duplicateRca(rcaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          row.rca_id = response?.rca_id || row.rca_id;
          const actionItems = {
            row: row,
            action: 'Duplicate Draft',
          };
          this.loadSignupRequests({ first: 0, rows: this.requestPageSize });
          this.onToggleAction(actionItems);
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to duplicate RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to duplicate RCA. Please try again.",
            life: 5000,
          });
        },
      });
  }

  private isCurrentUserShared(row: any): boolean {
    if (this.currentUserId === null || this.currentUserId === undefined) {
      return false;
    }
    if (!Array.isArray(row?.shared_user_ids)) {
      return false;
    }
    const currentId = String(this.currentUserId);
    return row.shared_user_ids.some((id: any) => {
      if (id === null || id === undefined) {
        return false;
      }
      return String(id) === currentId;
    });
  }

  private executeRevise(row: ManageAssessment): void {
    const event = {
      row,
      action: "Revise",
    };
    this.resumeRca.emit(event);
  }

  private executeMarkAsCompleted(row: ManageAssessment): void {
    this.manageAssessmentApiService
      .completeManageAssessment(row.rca_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.onShowSuccessPopup({
            isSuccess: true,
            title: "Mark as Completed",
            message: response.message,
            rcaData: response,
          })
          this.loadSignupRequests();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to mark RCA as completed:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to mark RCA as completed. Please try again.",
            life: 5000,
          });
        },
      });
  }

  private executeCancelAssessment(row: ManageAssessment): void {
    this.manageAssessmentApiService
      .cancelManageAssessment(row.rca_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.loadSignupRequests();
          this.onShowSuccessPopup({
            isSuccess: true,
            title: "Cancel Assessment",
            message: response.message,
            rcaData: response,
          })
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to cancel RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to cancel RCA. Please try again.",
            life: 5000,
          });
        },
      });
  }

  // This method will be called by the app-table whenever the selection changes
  onSelectedItemsChange(selectedItems: any[]): void {
    this.selectedAssessmentItems = selectedItems;
    // Update the visibility of the action button based on selection count
    this.showBulkActionButton = this.selectedAssessmentItems.length > 0;
    this.cdr.detectChanges();
  }

  handleBulkAction(): void {
    if (this.selectedAssessmentItems.length > 0) {
      this.showConfirmation("no_impact", this.selectedAssessmentItems);
    } else {
      this.messageService.add({
        severity: "warn",
        summary: "No Selection",
        detail: "Please select at least one item to perform a bulk action.",
        life: 3000,
      });
    }
  }

  /**
   * Displays the success popup with provided data.
   * @param popupData Data to be displayed in the success popup.
   */
  onShowSuccessPopup(popupData: any): void {
    this.successPopupData = popupData;
    this.successPopupVisible = true;
    this.cdr.detectChanges();
  }

  /**
   * Handles closing the success popup.
   */
  onSuccessPopupClose(): void {
    this.successPopupVisible = false;
    this.cdr.detectChanges();
  }

  onSharePopupClose(): void {
    this.showSharePopup = false;
    this.cdr.detectChanges();
  }

  debouncedSearch(event: FilterOptionsLazyLoadEvent): void {
    this.filterOptionsLazyLoad$.next(event);
  }

}